import { useCallback, useRef, useState } from 'react';
import { ModalLayout, SearchableSelect, Button } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { useWorkspaceUsers } from '../../hooks/useWorkspaceUsers.js';
import { UserCog } from 'lucide-react';

/**
 * Reassign a My Team / task event to another owner. The picker loads the REAL
 * workspace directory (CT-5094) via Platform API
 * POST /v1/workspace/user/list_filter — the selected option carries the real user uuid, which the reassign call
 * (CT-5093, POST /v1/task/reassign) needs as assigneeUuid.
 */
function ReassignModal({ isOpen, event, onClose, onReassign }) {
  const { t } = useTranslation('work-queues-app');
  const [assignee, setAssignee] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const submittingRef = useRef(false);
  // `[assignee?.id]` (selectedIds) keeps a just-picked assignee from vanishing
  // from `options` when the search that surfaced them clears and refetches.
  const { users, loading, error } = useWorkspaceUsers(isOpen, search, assignee?.id ? [assignee.id] : undefined);

  const handleSubmit = useCallback(async () => {
    if (!assignee || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const ok = await onReassign(event, assignee);
      if (!ok) return;
      setAssignee(null);
      setSearch('');
      onClose();
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [assignee, event, onClose, onReassign]);

  // Reset the selection on every close path (Cancel, overlay, Esc, X) so a
  // reopen for a different event never starts with the previous person picked.
  const handleClose = useCallback(() => {
    if (submittingRef.current) return;
    setAssignee(null);
    setSearch('');
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  const pickerDisabled = loading || Boolean(error) || submitting;

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={handleClose}
      useBodyPortal
      titlePrefix={<UserCog size={20} className="text-sky-500" />}
      breadcrumb={event ? `${event.id} · ${event.refLabel ?? ''}` : undefined}
      title={t('team.reassignTitle')}
      subtitle={t('team.reassignSubtitle')}
    >
      <div className="flex flex-col gap-4 px-6 py-4">
        <div role="group" aria-labelledby="reassign-assignee-label">
          <p
            id="reassign-assignee-label"
            className="mb-1.5 block text-sm font-medium text-content"
          >
            {t('team.reassignLabel')}
          </p>
          <SearchableSelect
            options={users}
            value={assignee?.id ?? null}
            onSelect={setAssignee}
            onChange={setSearch}
            showAvatar={false}
            disabled={pickerDisabled}
            placeholder={loading ? t('team.reassignLoading') : t('team.reassignPlaceholder')}
            searchPlaceholder={t('team.reassignSearch')}
          />
          {error && <p className="mt-1.5 text-xs text-rose-500">{t('team.reassignLoadError')}</p>}
          {!loading && !error && users.length === 0 && (
            <p className="mt-1.5 text-xs text-content-faint">{t('team.reassignEmpty')}</p>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 border-t border-edge px-6 py-4">
        <Button variant="secondary" size="sm" disabled={submitting} onClick={handleClose}>
          {t('actions.cancel')}
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={!assignee || submitting}
          onClick={handleSubmit}
        >
          {submitting ? t('team.reassignSubmitting') : t('team.reassign')}
        </Button>
      </div>
    </ModalLayout>
  );
}

export default ReassignModal;
